// AuthProvider / useCurrentUser / handleUnauthorized (RFC-0009 Task 2.1, B4).
//
// This is the authenticated shell's session layer. It does two things, BOTH
// scoped to the AppShell subtree (it mounts only inside PortalLayout, never on
// /login — see layouts/portal-layout.tsx and RFC-0009 §2/§4):
//
//   1. App-mount session bootstrap — `useCurrentUser` runs `GET /api/me` so the
//      shell knows who is signed in (used for the nav identity, Task 2.3).
//   2. The 401 mid-session interceptor — on mount it registers
//      `handleUnauthorized` via `ApiClient.setUnauthorizedHandler` and clears it
//      on unmount, so a 401 from ANY `/api/*` read redirects to
//      `/login?return_to=<current relative path>`.
//
// Because AuthProvider never mounts on /login, `GET /api/me` never fires there
// and no protected 401 can be raised from that page — the structural half of the
// redirect-loop guard. `handleUnauthorized` adds two belt-and-suspenders guards:
//   - a no-op when the current path is already /login (so a stray 401 can never
//     produce `/login → /login?return_to=/login`), and
//   - a module-level in-flight boolean that de-dupes concurrent 401s so several
//     in-flight requests failing at once fire exactly one redirect.
//
// Note: exporting the provider alongside its hooks/helpers (useCurrentUser,
// useAuth, identityLabel) triggers the same accepted `react-refresh/
// only-export-components` *warning* as context/theme.tsx and context/connect-
// repo.tsx — lint still passes; this mirrors the repo pattern.
//
// Navigation goes through react-router (`useNavigate`) rather than a raw
// `window.location` assignment: /login is a react-router route (App.tsx) and the
// login page reads `return_to` via react-router (`useSearchParams`), so staying
// on the router keeps this consistent with how the existing return_to flow works
// (and testable under MemoryRouter). Under BrowserRouter, react-router's location
// mirrors `window.location`, so the captured path matches the RFC's contract.

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiGet, setUnauthorizedHandler } from '@/lib/api-client';
import { sanitizeReturnTo } from '@/lib/auth-api';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The authenticated identity echoed by RFC-0005's `GET /api/me` (the resolved
 * `req.identity`). Declared web-locally because the API-side copy lives in
 * `apps/api/src/auth/*` and cannot cross the app boundary. **`name` is an
 * OPTIONAL NESTED object** — Kratos only supplies it when Google returns given/
 * family name — so consumers must handle `name?.first`/`name?.last`/absent and
 * fall back to `email` (see `identityLabel`).
 */
export interface AuthenticatedIdentity {
  id: string;
  email: string;
  name?: { first?: string; last?: string };
}

interface AuthContextValue {
  /** The signed-in identity, or `undefined` while loading / unauthenticated. */
  identity: AuthenticatedIdentity | undefined;
  /** True while the `GET /api/me` bootstrap is in flight. */
  isLoading: boolean;
  /** True once an identity has resolved. */
  isAuthenticated: boolean;
}

// ---------------------------------------------------------------------------
// Query key + hook
// ---------------------------------------------------------------------------

const authKeys = {
  me: ['me'] as const,
};

/**
 * `GET /api/me` — the current authenticated identity. Wraps `apiGet` (inheriting
 * the shared no-retry-on-401/4xx predicate) so an expired session settles as an
 * error on the first failure and its 401 fires `handleUnauthorized` immediately.
 */
export function useCurrentUser(): UseQueryResult<AuthenticatedIdentity, Error> {
  return useQuery({
    queryKey: authKeys.me,
    queryFn: () => apiGet<AuthenticatedIdentity>('/api/me'),
  });
}

// ---------------------------------------------------------------------------
// Display helper
// ---------------------------------------------------------------------------

/**
 * Resolve the label to show for an identity: the full name when Kratos supplied
 * one (`first`, or `first last`), otherwise the email. Returns `undefined` when
 * there is no identity yet. Trims to ignore blank name parts.
 */
export function identityLabel(
  identity: AuthenticatedIdentity | undefined,
): string | undefined {
  if (!identity) return undefined;
  const first = identity.name?.first?.trim();
  const last = identity.name?.last?.trim();
  const full = [first, last].filter(Boolean).join(' ');
  return full || identity.email;
}

// ---------------------------------------------------------------------------
// Interceptor de-dupe flag (module-level, RFC-0009 §4)
// ---------------------------------------------------------------------------

// Guards against several concurrent in-flight requests all 401-ing at once and
// stacking duplicate redirects. Reset on every AuthProvider mount/unmount so a
// fresh shell mount always starts clean.
let redirecting = false;

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const AuthContext = createContext<AuthContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function AuthProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Read the CURRENT location at 401 time (not a stale closure): keep a ref in
  // sync each render so `handleUnauthorized` stays a stable callback.
  const locationRef = useRef(location);
  locationRef.current = location;

  // App-mount session bootstrap (runs only inside the AppShell subtree).
  const query = useCurrentUser();

  const handleUnauthorized = useCallback(() => {
    const { pathname, search } = locationRef.current;
    // Belt-and-suspenders: never redirect a 401 that arrived while already on
    // /login (no `/login → /login?return_to=/login` loop).
    if (pathname === '/login') return;
    // De-dupe concurrent 401s into a single redirect.
    if (redirecting) return;
    redirecting = true;

    const current = `${pathname}${search}`;
    const safe = sanitizeReturnTo(current);
    const target = safe
      ? `/login?return_to=${encodeURIComponent(safe)}`
      : '/login';
    // `replace` so the expired page is not left in history (no back-button loop
    // back into another 401).
    navigate(target, { replace: true });
  }, [navigate]);

  // Register the global 401 handler for the lifetime of the shell; clear it on
  // unmount so /login (rendered after the shell unmounts) has no active handler.
  useEffect(() => {
    redirecting = false;
    setUnauthorizedHandler(handleUnauthorized);
    return () => {
      setUnauthorizedHandler(null);
      redirecting = false;
    };
  }, [handleUnauthorized]);

  const value = useMemo<AuthContextValue>(
    () => ({
      identity: query.data,
      isLoading: query.isLoading,
      isAuthenticated: Boolean(query.data),
    }),
    [query.data, query.isLoading],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the authenticated identity. Returns safe defaults when called outside
 * an AuthProvider (never throws), matching theme.tsx / connect-repo.tsx.
 */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (ctx) return ctx;
  return { identity: undefined, isLoading: false, isAuthenticated: false };
}
